export const COST_SCENARIO_MAU = [10_000, 100_000, 1_000_000];

export function formatMoney(value, currency = 'USD', locale = 'en-US', maximumFractionDigits = 0) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits,
  }).format(Number(value));
}

export function latestGaBaseline(rows, fallback = {}) {
  const ordered = [...(rows || [])].filter((row) => row?.date).sort((a, b) => a.date.localeCompare(b.date));
  const latest = ordered.at(-1) || {};
  const eventTotal = ordered.reduce((total, row) => total + Number(row.eventCount || 0), 0);
  return {
    dau: Number(latest.dau || latest.active1DayUsers || fallback.dau || 0),
    mau: Number(latest.mau || latest.active28DayUsers || fallback.mau || 0),
    events: Number(eventTotal || fallback.events || 0),
  };
}

export function projectionFactor(item, targetMau, baseline) {
  if (item.driver === 'fixed') return 1;
  const baseMau = Number(baseline?.mau || 0);
  if (!baseMau || !targetMau) return null;
  return targetMau / baseMau;
}

export function projectLineItem(item, targetMau, baseline) {
  const factor = projectionFactor(item, targetMau, baseline);
  if (factor == null) return null;
  const monthlyCost = Number(item.monthly_cost || 0);
  const fixedRatio = item.driver === 'fixed' ? 1 : Math.min(1, Math.max(0, Number(item.fixed_ratio || 0)));
  const elasticity = Number(item.elasticity || 1) || 1;
  return monthlyCost * (fixedRatio + (1 - fixedRatio) * (factor ** elasticity));
}

export function buildCostScenarios(snapshot, baseline, targets = COST_SCENARIO_MAU) {
  const items = snapshot?.line_items || [];
  return targets.map((mau) => {
    const costs = items.map((item) => projectLineItem(item, mau, baseline));
    const available = costs.every((cost) => cost != null);
    return {
      mau,
      cost: available ? costs.reduce((total, cost) => total + cost, 0) : null,
      kind: 'estimated',
    };
  });
}

export function allocationScope(item) {
  if (item?.classification?.allocation_scope) return item.classification.allocation_scope;
  return item?.capabilities?.length ? 'feature' : 'unallocated';
}

export function allocationCoverage(items = [], scope = 'feature') {
  const total = items.reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0);
  const allocated = items
    .filter((item) => allocationScope(item) === scope)
    .reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0);
  return total > 0 ? allocated / total : 0;
}

export function projectionRuleCoverage(items = []) {
  const total = items.reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0);
  const classified = items
    .filter((item) => item.classification?.projection_rule && item.classification.projection_rule !== 'unclassified-held-fixed')
    .reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0);
  return total > 0 ? classified / total : 0;
}

export function snapshotAgeDays(snapshot, now = new Date()) {
  const value = snapshot?.source?.collected_at || snapshot?.period?.to;
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, Math.floor((now.getTime() - timestamp) / 86_400_000));
}

export function sortLineItems(items = []) {
  return [...items].sort((a, b) => Number(b.monthly_cost || 0) - Number(a.monthly_cost || 0));
}
